import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import { Layout } from './components/Layout'
import { Loading } from './components/States'
import { LoginPage } from './pages/LoginPage'
import { ChangePasswordPage } from './pages/ChangePasswordPage'
import { CommissionPage } from './pages/CommissionPage'
import { DashboardPage } from './pages/DashboardPage'
import { InvoicesPage } from './pages/InvoicesPage'
import { InvoiceDetailPage } from './pages/InvoiceDetailPage'
import { ProductsPage } from './pages/ProductsPage'
import { UncataloguedPage } from './pages/UncataloguedPage'
import { ProductDetailPage } from './pages/ProductDetailPage'
import { CustomersPage } from './pages/CustomersPage'
import { CustomerDetailPage } from './pages/CustomerDetailPage'
import { SalesTeamPage } from './pages/SalesTeamPage'
import { SalesPersonDetailPage } from './pages/SalesPersonDetailPage'
import { StaffPage } from './pages/StaffPage'
import { AppVersionsPage } from './pages/AppVersionsPage'
import { UsagePage } from './pages/UsagePage'
import { SystemStatusPage } from './pages/SystemStatusPage'
import { DeliveriesTodayPage } from './pages/DeliveriesTodayPage'
import { DeliveryAnalyticsPage } from './pages/DeliveryAnalyticsPage'
import { PickupAnalyticsPage } from './pages/PickupAnalyticsPage'
import { PickupLivePage } from './pages/PickupLivePage'
import { DeliveryInboxPage } from './pages/DeliveryInboxPage'
import { DeliveryPlanPage } from './pages/DeliveryPlanPage'

// The demo carries the map library; load it only when someone opens the demo.
const DeliveryDemoPage = lazy(() =>
  import('./pages/DeliveryDemoPage').then((module) => ({ default: module.DeliveryDemoPage })),
)

/** Admin-only pages; a coordinator who lands on one goes to the delivery board. */
function AdminOnly() {
  const { isAdmin } = useAuth()
  return isAdmin ? <Outlet /> : <Navigate to="/deliveries/today" replace />
}

export default function App() {
  const { session, isAdmin } = useAuth()

  if (!session) return <LoginPage />

  // A freshly created or reset account has to pick a password before it can do
  // anything else — same rule the app enforces.
  if (session.mustChangePassword) return <ChangePasswordPage />

  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Coordinators and admins */}
        <Route path="/deliveries/today" element={<DeliveriesTodayPage />} />
        <Route path="/deliveries/inbox" element={<DeliveryInboxPage />} />
        <Route path="/deliveries/plan" element={<DeliveryPlanPage />} />
        <Route path="/deliveries/analytics" element={<DeliveryAnalyticsPage />} />
        <Route
          path="/deliveries/demo"
          element={
            <Suspense fallback={<Loading />}>
              <DeliveryDemoPage />
            </Suspense>
          }
        />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />

        <Route element={<AdminOnly />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/pickups/live" element={<PickupLivePage />} />
          <Route path="/pickups/analytics" element={<PickupAnalyticsPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/uncatalogued" element={<UncataloguedPage />} />
          <Route path="/products/:sku" element={<ProductDetailPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:customerId" element={<CustomerDetailPage />} />
          <Route path="/sales-team" element={<SalesTeamPage />} />
          <Route path="/sales-team/:person" element={<SalesPersonDetailPage />} />
          <Route path="/commission" element={<CommissionPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/app-versions" element={<AppVersionsPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/system" element={<SystemStatusPage />} />
        </Route>

        <Route
          path="*"
          element={<Navigate to={isAdmin ? '/dashboard' : '/deliveries/today'} replace />}
        />
      </Route>
    </Routes>
  )
}
